import type {StudentIdCard} from '@/utils/IdCard.ts';
import type * as React from 'react';
import SvgText from '../../../components/SvgText';
import Barcode from '../Barcode';

type Props = {
    idCard: StudentIdCard;
    width: string;
};

const StudentFront: React.FC<Props> = ({idCard, width = '100%'}: Props): React.ReactElement => {
    return (
        <svg viewBox='0 0 1200 800' width={width}>
            <title id={`student-front-${idCard.id}`}>Student Front for ID {idCard.id}</title>
            <defs>
                <clipPath id='card'>
                    <rect x={0} y={0} width={1200} height={800} rx={50} ry={50} />
                </clipPath>
            </defs>

            <image
                href={`data:image/jpeg;base64,${idCard.backgroundImage}`}
                width={1200}
                height={800}
                x={0}
                y={0}
                clipPath='url(#card)'
                preserveAspectRatio='xMinYMin slice'
            />

            <image
                href={`data:image/jpeg;base64,${idCard.schoolBanner}`}
                width={375}
                height={165}
                x={730}
                y={35}
            />

            <rect x={60} y={60} width={300} height={450} fill='white' />
            <image
                href={`data:image/jpeg;base64,${idCard.photo}`}
                width={300}
                height={450}
                x={60}
                y={60}
            />

            <rect x={60} y={600} width={680} height={160} fill='white' />
            <Barcode idCard={idCard} transform='translate(80, 610)' withoutRoot height={90} />

            <text x={60} y={580} fontSize={50} fontWeight='bold' fill={`#${idCard.textColor}`}>
                {idCard.name}
            </text>

            <text
                x={300 + 60 + 60}
                y={450 / 2 + 60}
                fontSize={50}
                fontWeight='bold'
                fill={`#${idCard.textColor}`}
                transform={`rotate(-90, ${300 + 60 + 60}, ${450 / 2 + 60})`}
                textAnchor='middle'
            >
                <tspan>{idCard.schoolYear}</tspan>
                <tspan x={300 + 60 + 60} dy={60}>
                    {idCard.grade}
                </tspan>
            </text>

            {idCard.lunchPeriods && (
                <>
                    <text
                        x={917.5}
                        y={270}
                        fontSize={50}
                        fontWeight='bold'
                        textDecoration='underline'
                        fill={`#${idCard.textColor}`}
                        textAnchor='middle'
                    >
                        1st Semester
                    </text>
                    <text
                        x={917.5 - 110}
                        y={270 + 70}
                        fontSize={50}
                        fontWeight='bold'
                        fill={`#${idCard.textColor}`}
                        textAnchor='middle'
                    >
                        <tspan>{idCard.dayNames.aDay}</tspan>
                        <tspan x={917.5 - 110} dy={50}>
                            {idCard.lunchPeriods.semesterOneDayA}
                        </tspan>
                    </text>
                    <text
                        x={917.5 + 110}
                        y={270 + 70}
                        fontSize={50}
                        fontWeight='bold'
                        fill={`#${idCard.textColor}`}
                        textAnchor='middle'
                    >
                        <tspan>{idCard.dayNames.bDay}</tspan>
                        <tspan x={917.5 + 110} dy={50}>
                            {idCard.lunchPeriods.semesterOneDayB}
                        </tspan>
                    </text>
                    <text
                        x={917.5}
                        y={460}
                        fontSize={50}
                        fontWeight='bold'
                        textDecoration='underline'
                        fill={`#${idCard.textColor}`}
                        textAnchor='middle'
                    >
                        2nd Semester
                    </text>
                    <text
                        x={917.5 - 110}
                        y={460 + 70}
                        fontSize={50}
                        fontWeight='bold'
                        fill={`#${idCard.textColor}`}
                        textAnchor='middle'
                    >
                        <tspan>{idCard.dayNames.aDay}</tspan>
                        <tspan x={917.5 - 110} dy={50}>
                            {idCard.lunchPeriods.semesterTwoDayA}
                        </tspan>
                    </text>
                    <text
                        x={917.5 + 110}
                        y={460 + 70}
                        fontSize={50}
                        fontWeight='bold'
                        fill={`#${idCard.textColor}`}
                        textAnchor='middle'
                    >
                        <tspan>{idCard.dayNames.bDay}</tspan>
                        <tspan x={917.5 + 110} dy={50}>
                            {idCard.lunchPeriods.semesterTwoDayB}
                        </tspan>
                    </text>
                </>
            )}

            {idCard.activityPass && (
                <>
                    <rect
                        x={880}
                        y={640}
                        width={280}
                        height={120}
                        rx={20}
                        ry={20}
                        strokeWidth={10}
                        stroke={`#${idCard.activityPass.borderColor}`}
                        fill={`#${idCard.activityPass.fillColor}`}
                    />
                    <SvgText
                        blocks={[idCard.activityPass.text]}
                        x={880 + 280 / 2}
                        y={640 + 120 / 2 - 55}
                        fontSize={50}
                        fontWeight='bold'
                        width={260}
                        textAnchor='middle'
                        fill={`#${idCard.activityPass.textColor}`}
                    />
                </>
            )}
        </svg>
    );
};

export default StudentFront;
